import _ from 'underscore';
import moment from 'moment';
import {createSelector} from 'reselect';

import {rideStatus} from 'common/types/Ride';
import {serviceTypes} from 'common/types/Service';
import {combineGeometries, parseGeometry, rideDirections} from 'common/utils/geo';
import {forceTimezone} from 'common/utils/time';


export const getServiceLoadThresholds = () => ({medium: 15, high: 30});

export const getActiveAgencyId = (state) => state.settings.agencyId;

export const getActiveServiceId = (state) => state.settings.serviceId;

export const getAgencies = (state) => state.agency.items || {};

export const getFetching = state =>
  Object.assign(
    {},
    _.pick(state, item => item.isFetching),
    _.pick(state.ride, item => item && item.isFetching) // rideRequest is nested in ride
  );

export const getRegions = (state) => state.region.items;

export const getServices = (state) => state.service.items;

export const getServiceEstimate = (state) => state.serviceEstimate.item;

export const getServiceGeometries = (state) => state.serviceGeometries.info;

export const getAgencyAddresses = (state) => state.agencyAddress.items;

export const getAgencySettings = (state) => state.agencySetting.items[1] || {};

export const getPrivacyPolicyAccepted = (state) => state.privacyPolicy.accepted;

export const getRiderAddresses = (state) => state.riderAddress.items;

export const getRiderAddressDraft = (state) => state.riderAddress.draft.present.model;

export const getRiderAddressDraftErrors = (state) => state.riderAddress.draft.present.errors;

// Rider limits + current usage for a service (from the rider limits endpoint), or null if not
// loaded yet. Each item is {limit_type, limit_value, timeframe, timeframe_value, current_usage}.
export const getServiceLimits = (state, serviceId) =>
  ((state.serviceLimits[serviceId] || {}).limits || null);

export const getRides = (state) => state.ride.items;

export const getRideDetails = (state) => state.rideDetails.item;

export const getRideError = (state) => state.ride.error;

export const getRideRequest = (state) => state.ride.rideRequest;

export const getVehiclePosition = (state) => state.vehiclePosition.item;

export const getCurrentTime = () => moment().utc();

export const getActiveAgency = createSelector(
  getActiveAgencyId,
  getAgencies,
  (agencyId, agencies) => agencies[agencyId] || null
);

export const getActiveService = createSelector(
  getActiveServiceId,
  getServices,
  (serviceId, services) => services[serviceId] || null
);

export const getActiveServiceLoad = createSelector(
  getServiceEstimate,
  getServiceLoadThresholds,
  (estimate, thresholds) => {
    if (!estimate || !estimate.estimated_wait_time) {
      return 'unknown';
    } else if (estimate.estimated_wait_time < thresholds.medium * 60) {
      return 'low';
    } else if (estimate.estimated_wait_time < thresholds.high * 60) {
      return 'medium';
    }
    return 'high';
  }
);

export const getActiveRides = createSelector(
  getRides,
  getAgencies,
  getCurrentTime,
  (rides, agencies, currentTime) => _.filter(rides, (ride) => {
    if (!agencies[ride.agencyId]) {
      return false;
    }

    return _.all([
      Math.abs(currentTime.diff(ride.pickup.time, 'seconds')) <= 3600,
      _.contains([
        rideStatus.NEEDS_APPROVAL,
        rideStatus.PENDING,
        rideStatus.IN_PROGRESS,
      ], ride.status),
    ]);
  })
);

function isExpired(timezone, service) {
  const today = moment.tz(timezone).startOf('day');
  const endDate = forceTimezone(timezone, moment(service.end_date));
  if (endDate.isBefore(today)) {
    const futureService = _.chain(service.exceptions)
      .where({type: 'service_added'})
      .filter(exc => !forceTimezone(timezone, moment(exc.end_time)).isBefore(today))
      .value();
    if (_.size(futureService) === 0) {
      return true;
    }
  }
  return false;
}

function isArchived(timezone, service) {
  if (service.archived_at) {
    return true;
  }
  return false;
}

export const getAvailableServices = createSelector(
  getActiveAgency,
  getServices,
  getActiveRides,
  (agency, services, activeRides) => _.filter(services, (service) => {
    if (!service.ride_request_allowed) {
      return false;
    }

    if (!agency || !agency.timezone) {
      return false;
    }

    if (isExpired(agency.timezone, service)) {
      return false;
    }

    if (isArchived(agency.timezone, service)) {
      return false;
    }

    if (_.any([
      service.service_type === serviceTypes.INADVANCE,
      service.in_advance_enabled,
    ])) {
      return true;
    }

    return !_.size(activeRides) && service.in_service;
  })
);

export const getUnavailableServices = createSelector(
  getActiveAgency,
  getServices,
  getAvailableServices,
  (agency, services, availableServices) => _.filter(services, (service) => {
    const availableServiceIds = _.pluck(availableServices, 'service_id');
    return !_.contains(availableServiceIds, service.service_id) &&
      agency && !isExpired(agency.timezone, service) &&
      !isArchived(agency.timezone, service);
  })
);

export const getActiveServices = createSelector(
  getActiveAgency,
  getServices,
  (agency, services) => _.filter(services, (service) => {
    return agency && !isExpired(agency.timezone, service) &&
      !isArchived(agency.timezone, service);
  })
);

export const getAgencyAddressList = createSelector(getAgencyAddresses, _.values);

export const getAllAddresses = createSelector(
  getAgencyAddresses,
  getRiderAddresses,
  (agencyAddresses, riderAddresses) =>
    _.map(agencyAddresses, (address) => Object.assign({}, address, {type: 'agency'}))
      .concat(_.map(riderAddresses, (address) => Object.assign({}, address, {type: 'rider'})))
);

export const getSortedRiderAddresses = createSelector(
  getRiderAddresses,
  (riderAddresses) =>
    (riderAddresses ? _.sortBy(riderAddresses, address => address.name.toLowerCase()) : null)
);

export const getPickupAddresses = createSelector(
  getActiveService,
  getAllAddresses,
  (service, addresses) => {
    const serviceAddressRules = service ? service.addresses : [];
    const serviceAddresses =
      _.filter(_.map(serviceAddressRules, (rule) =>
        _.find(addresses, a => a.addressId === rule.address_id && rule.pickup)), _.identity);
    return serviceAddresses.concat(_.filter(addresses, (a) => a.type === 'rider'));
  }
);

export const getDropoffAddresses = createSelector(
  getActiveService,
  getAllAddresses,
  (service, addresses) => {
    const serviceAddressRules = service ? service.addresses : [];
    const serviceAddresses =
      _.filter(_.map(serviceAddressRules, (rule) =>
        _.find(addresses, a => a.addressId === rule.address_id && rule.dropoff)), _.identity);
    return serviceAddresses.concat(_.filter(addresses, (a) => a.type === 'rider'));
  }
);

export const getSelectableAddresses = createSelector(
  getPickupAddresses,
  getDropoffAddresses,
  (pickup, dropoff) => _.uniq(pickup.concat(dropoff), _.iteratee('addressId'))
);

export const getPickupArea = createSelector(
  getServiceGeometries,
  (serviceGeometries) =>
    serviceGeometries &&
    serviceGeometries.pickup &&
    parseGeometry(serviceGeometries.pickup)
);

export const getDropoffArea = createSelector(
  getServiceGeometries,
  (serviceGeometries) =>
    serviceGeometries &&
    serviceGeometries.dropoff &&
    parseGeometry(serviceGeometries.dropoff)
);

export const getServiceArea = createSelector(
  getPickupArea,
  getDropoffArea,
  (pickup, dropoff) => pickup && dropoff && combineGeometries([pickup, dropoff])
);

export const getRideRequestDirections = createSelector(
  getActiveService,
  getRideRequest,
  (service, {pickup, dropoff, pickupName, dropoffName}) => {
    if (!service || !pickup || !dropoff) return null;
    const origin = {name: pickupName, position: pickup};
    const destination = {name: dropoffName, position: dropoff};
    return rideDirections(origin, destination);
  }
);



// WEBPACK FOOTER //
// ./src/ondemand/selectors/index.js